import pick from 'lodash/pick';

import {purgeFetchCachePathsMatchingRegex} from 'common/report/queries/utils/fetch-cache';
import {apiClient} from 'common/util/api-client';
import {GlobalAccessParamName} from 'common/report/util/saved-entities/types';
import {SuccessResponse} from 'common/util/shared-client';
import {GetDashboardTimezoneResponse, Results as DashboardTimezoneResults} from 'app_api/projects/dashboards/types';

import {BaseClient, QueryOptions} from '../utils/base-client';
import {NamedError} from '../utils/named-error';
import {Dashboard} from './dashboard-types';

class FieldStringTooLongError extends NamedError {
  constructor(message, public fieldName: string, public fieldLength: string, public maxLength: string) {
    super(message);
  }

  static get type() {
    return `FieldStringTooLong`;
  }
}

class PermissionDeniedError extends NamedError {
  static get type() {
    return `PermissionDenied`;
  }
}

class NotImplementedError extends NamedError {
  static get type() {
    return `NotImplemented`;
  }
}

class InvalidParamsError extends NamedError {
  static get type() {
    return `InvalidParams`;
  }
}

class DashboardDoesNotExistError extends NamedError {
  static get type() {
    return `DashboardDoesNotExist`;
  }
}

class CardDoesNotExistError extends NamedError {
  static get type() {
    return `CardDoesNotExist`;
  }
}

class CardOrderOutOfSyncError extends NamedError {
  static get type() {
    return `CardOrderOutOfSync`;
  }
}

class BookmarkDoesNotExistError extends NamedError {
  static get type() {
    return `BookmarkDoesNotExist`;
  }
}

type ProjectId = string | number;

// TODO: refactor to use use xId rather than xID
interface BuildEndpointParams {
  projectId?: ProjectId;
  dashboardID?: number;
  bookmarkID?: number;
  erf?: boolean;
  emailDigest?: boolean;
  workspaceId?: number;
  favorites?: boolean;
  pin?: boolean;
}

type DashboardFields = Partial<Pick<Dashboard, 'title' | 'description' | 'filters' | 'time_filter'>>;

const VALID_DASHBOARD_PARAMS = [
  `title`,
  `description`,
  `time_filter`,
  `filters`,
  `card_order`,
  `cards`,
  `duplicate`,
  GlobalAccessParamName,
];
const VALID_DASHBOARD_CARD_PARAMS = [`id`, `width`, `title`, `chart_options`, `action`, `bookmark_id`];
/* Dashboards 2.0 API is documented here: https://github.com/mixpanel/analytics/blob/master/app_api/projects/dashboards/README.md */
class DashboardsClient extends BaseClient {
  arrayFormatBracket = true;

  buildEndpoint({
    projectId,
    dashboardID,
    bookmarkID,
    erf,
    emailDigest,
    workspaceId,
    favorites,
    pin,
  }: BuildEndpointParams) {
    const _projectId = projectId;
    const baseUrl = workspaceId ? `api/app/workspaces/${workspaceId}` : `api/app/projects/${_projectId}`;
    if (dashboardID) {
      if (erf) {
        return `${baseUrl}/dashboards/${dashboardID}/erf`;
      }
      if (favorites) {
        return `${baseUrl}/dashboards/${dashboardID}/favorites/`;
      }
      if (pin) {
        return `${baseUrl}/dashboards/${dashboardID}/pin/`;
      }
      return `${baseUrl}/dashboards/${dashboardID}/`;
    }
    if (bookmarkID) {
      return `${baseUrl}/dashboards/bookmarks/${bookmarkID}/dashboard-ids/`;
    }
    if (emailDigest) {
      return `${baseUrl}/dashboard-reports/email-digests`;
    }

    return `${baseUrl}/dashboards/`;
  }

  buildQueryParams(params) {
    const dashboardIds = params.dashboardIds ?? [];
    return {
      id: dashboardIds,
    };
  }

  /** Sends a HTTP GET request to retrieve all dashboards for a specified project */
  queryAll(
    {
      projectId,
      dashboardIds,
      workspaceId,
    }: {
      projectId?: ProjectId;
      dashboardIds?: number[];
      workspaceId?: number;
    },
    queryOpts?: QueryOptions,
  ): {cached: Promise<Dashboard[] | null>; network: Promise<Dashboard[]>} {
    const params = {projectId, workspaceId, dashboardIds};
    return {
      cached: this.query(params, {...queryOpts, fromFetchCache: true}),
      network: this.query(params, {...queryOpts, updateFetchCache: true}),
    };
  }

  /** Sends a HTTP GET request to retrieve a specific dashboard */
  querySingle(
    {
      projectId,
      dashboardID,
      workspaceId,
    }: {
      projectId: ProjectId;
      dashboardID: number;
      workspaceId?: number;
    },
    queryOpts?: QueryOptions,
  ): {cached: Promise<Dashboard | null>; network: Promise<Dashboard>} {
    const params = {projectId, dashboardID, workspaceId};
    return {
      cached: this.query(params, {...queryOpts, fromFetchCache: true}),
      network: this.query(params, {...queryOpts, updateFetchCache: true}),
    };
  }

  /** Sends a HTTP GET request to retrieve a specific dashboard for ERF */
  querySingleERF(
    {
      projectId,
      dashboardID,
      workspaceId,
    }: {
      projectId: ProjectId;
      dashboardID: number;
      workspaceId?: number;
    },
    queryOpts?: QueryOptions,
  ): Promise<any> {
    return this.query({projectId, dashboardID, workspaceId, erf: true}, queryOpts);
  }

  async queryTimezone(endpointData: {projectId: ProjectId; workspaceId?: number}): Promise<DashboardTimezoneResults> {
    const url = `/${this.buildEndpoint(endpointData)}timezone`;
    const result = await this.processResponse(
      await this.wrapErrorThrower(apiClient.get<GetDashboardTimezoneResponse>(url)),
    );
    return result;
  }

  queryEmailDigests(
    {projectId, workspaceId}: {projectId: ProjectId; workspaceId?: number},
    queryOpts?: QueryOptions,
  ): Promise<any> {
    return this.query({projectId, workspaceId, emailDigest: true}, queryOpts);
  }

  /** Sends a HTTP GET request to retrieve dashboards containing cards cards with target bookmark */
  queryBookmarkDashboardIds(
    {
      projectId,
      bookmarkID,
      workspaceId,
    }: {
      projectId: ProjectId;
      bookmarkID: number;
      workspaceId?: number;
    },
    queryOpts?: QueryOptions,
  ): Promise<number[]> {
    return this.query({projectId, bookmarkID, workspaceId}, queryOpts);
  }

  /** Sends a HTTP PATCH request to update the fields of a dashboard */
  async patchUpdateDashboardFields({
    projectId,
    dashboardID,
    fields = {},
    workspaceId,
  }: {
    projectId: ProjectId;
    dashboardID: number;
    fields: DashboardFields;
    workspaceId?: number;
  }): Promise<Dashboard> {
    // global_access_type is an invalid parameter for PATCH call
    const VALID_PATCH_DASHBOARD_PARAMS = VALID_DASHBOARD_PARAMS.filter((param) => param !== GlobalAccessParamName);
    const patchData = pick(fields, VALID_PATCH_DASHBOARD_PARAMS);
    if (`cards` in patchData) {
      // @ts-ignore
      patchData.cards = patchData.cards.map((card) => pick(card, VALID_DASHBOARD_CARD_PARAMS));
    }

    const url = `/${this.buildEndpoint({projectId, dashboardID, workspaceId})}`;
    const result = await this.processResponse(await this.wrapErrorThrower(apiClient.patch(url, patchData)));
    await this.invalidateFetchCache();
    return result;
  }

  /** Sends a HTTP POST request to create a new dashboard */
  async postCreateDashboard({
    projectId,
    fields = {},
    workspaceId,
  }: {
    projectId: ProjectId;
    fields: DashboardFields;
    workspaceId?: number;
  }): Promise<Dashboard> {
    const postData: Partial<DashboardFields> = pick(fields, VALID_DASHBOARD_PARAMS);
    if (`cards` in postData) {
      // @ts-ignore
      postData.cards = postData.cards.map((card) => ({
        ...pick(card, VALID_DASHBOARD_CARD_PARAMS),
        bookmark_id: card.bookmark.id,
      }));
    }
    const url = `/${this.buildEndpoint({projectId, workspaceId})}`;
    const result = await this.processResponse(await this.wrapErrorThrower(apiClient.post(url, postData)));
    await this.invalidateFetchCache();
    return result;
  }

  /** Sends a HTTP POST request to add/remove cards with a bookmark from dashboards */
  async postChangeBookmarkDashboards({
    projectId,
    bookmarkID,
    added = [],
    removed = [],
    workspaceId,
  }: {
    projectId: ProjectId;
    bookmarkID: number;
    added: number[];
    removed: number[];
    workspaceId?: number;
  }): Promise<number[]> {
    if (added.length === 0 && removed.length === 0) {
      throw new Error(`arguments cannot be empty`);
    }

    const postData = added
      .map((id) => ({
        op: `add`,
        dashboard_id: id,
      }))
      .concat(
        removed.map((id) => ({
          op: `remove`,
          dashboard_id: id,
        })),
      );
    const url = `/${this.buildEndpoint({projectId, bookmarkID, workspaceId})}add-remove`;
    const result = await this.processResponse(await this.wrapErrorThrower(apiClient.post(url, postData)));
    await this.invalidateFetchCache();
    return result;
  }

  /** Sends a HTTP DELETE request to remove a dashboard */
  async deleteDashboard({
    projectId,
    dashboardID,
    workspaceId,
  }: {
    projectId: ProjectId;
    dashboardID: number;
    workspaceId?: number;
  }): Promise<Dashboard> {
    const url = `/${this.buildEndpoint({projectId, dashboardID, workspaceId})}`;
    const result = await this.processResponse(await this.wrapErrorThrower(apiClient.delete(url)));
    await this.invalidateFetchCache();
    return result;
  }

  /** Sends a HTTP POST request to remove multiple dashboards */
  async deleteDashboards({
    projectId,
    dashboardIds,
    workspaceId,
  }: {
    projectId: ProjectId;
    dashboardIds: number[];
    workspaceId?: number;
  }): Promise<SuccessResponse<string>> {
    const url = `/${this.buildEndpoint({projectId, workspaceId})}bulk-delete`;
    return this.processResponse(await apiClient.post(url, {dashboard_ids: dashboardIds}));
  }

  /** Marks dashboard as favorite */
  async favoriteDashboard({
    projectId,
    dashboardID,
    workspaceId,
    isFavorited,
  }: {
    projectId: ProjectId;
    dashboardID: number;
    workspaceId?: number;
    isFavorited: boolean;
  }) {
    const url = `/${this.buildEndpoint({projectId, dashboardID, workspaceId, favorites: true})}`;
    const result = await (isFavorited ? apiClient.post(url, {}) : apiClient.delete(url));
    await this.invalidateFetchCache();
    return result;
  }

  /** Pin dashboard to a project */
  async pinDashboard({
    projectId,
    dashboardID,
    workspaceId,
    isPinned,
  }: {
    projectId: ProjectId;
    dashboardID: number;
    workspaceId?: number;
    isPinned: boolean;
  }) {
    const url = `/${this.buildEndpoint({projectId, dashboardID, workspaceId, pin: true})}`;
    const result = await (isPinned ? apiClient.post(url, {}) : apiClient.delete(url));
    await this.invalidateFetchCache();
    return result;
  }

  async wrapErrorThrower(promise) {
    try {
      return await promise;
    } catch (error) {
      if (error.name === `ResponseError`) {
        const errorInfo = await error.response.json();
        this.throwApiError(errorInfo);
      } else {
        throw error;
      }
    }
  }

  throwApiError(apiResponse) {
    switch (apiResponse.type) {
      case FieldStringTooLongError.type:
        throw new FieldStringTooLongError(
          apiResponse.error,
          apiResponse[`field_name`],
          apiResponse[`field_length`],
          apiResponse[`max_length`],
        );
      case PermissionDeniedError.type:
        throw new PermissionDeniedError(apiResponse.error);
      case NotImplementedError.type:
        throw new NotImplementedError(apiResponse.error);
      case InvalidParamsError.type:
        throw new InvalidParamsError(apiResponse.error);
      case DashboardDoesNotExistError.type:
        throw new DashboardDoesNotExistError(apiResponse.error);
      case CardDoesNotExistError.type:
        throw new CardDoesNotExistError(apiResponse.error);
      case CardOrderOutOfSyncError.type:
        throw new CardOrderOutOfSyncError(apiResponse.error);
      case BookmarkDoesNotExistError.type:
        throw new BookmarkDoesNotExistError(apiResponse.error);
    }
  }

  processResponse(response) {
    if (response.error) {
      this.throwApiError(response);
    }

    // TODO: untangle BaseClient and use apiClient directly
    // BaseClient uses apiClient.fetch which returns top level object
    // apiClient.(get|put|post|patch|delete) return the value of response.results
    // we should use move away from using apiClient.fetch directly unless the server response
    // is not conformant to the api response format
    if (Object.prototype.hasOwnProperty.call(response, `results`)) {
      return response.results;
    }

    return response;
  }

  async invalidateFetchCache(): Promise<void> {
    // this gets us out of trouble for now, but in the future Dashboards should migrate to Store
    await purgeFetchCachePathsMatchingRegex(/^\/api\/app\/(?:workspaces|projects)\/\d+\/dashboard.*/);
  }
}

export const dashboardsClient = new DashboardsClient();
